import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { CaretDownIcon, XIcon } from "@phosphor-icons/react";

/** The menu's "no filter" item: a radio group needs a value for it. */
const ALL = "__all";

export type FilterOption<T extends string> = {
  value: T;
  label: React.ReactNode;
  /** How many rows the choice would show; drawn after its label. */
  count?: number;
};

/**
 * How a list is filtered, as pills: one pill per choice, the active one
 * filled. Lists filtered three ways (pills on the FAQ and the Messagerie,
 * Select boxes in the search bar of the calls, contacts and calendar) now look
 * alike: a list's own views are `FilterPills`, and a dimension with too many
 * values to lay out (the calls' type, channel and status in a 22rem pane) is
 * a `FilterMenuPill` — the same pill, which opens its choices.
 */
export function FilterPills<T extends string>({
  value,
  onChange,
  options,
  label,
  className,
}: {
  value: T;
  onChange: (value: T) => void;
  options: FilterOption<T>[];
  /** What the row filters, for screen readers. */
  label?: string;
  className?: string;
}) {
  return (
    <div
      role="group"
      aria-label={label}
      className={cn("flex flex-wrap gap-2", className)}
    >
      {options.map((option) => {
        const active = option.value === value;
        return (
          <Button
            key={option.value}
            type="button"
            size="sm"
            variant={active ? "default" : "outline"}
            aria-pressed={active}
            onClick={() => onChange(option.value)}
          >
            {option.label}
            {option.count !== undefined && (
              <span
                className={cn(
                  "tabular-nums",
                  active ? "opacity-80" : "text-muted-foreground",
                )}
              >
                {option.count}
              </span>
            )}
          </Button>
        );
      })}
    </div>
  );
}

/**
 * One filter as a pill that opens its choices. Off, it reads as what it
 * filters ("Type"); set, it is filled and reads as its value, with a cross
 * that clears it — the state of a filter has to be readable without opening
 * it.
 */
export function FilterMenuPill<T extends string>({
  label,
  value,
  onChange,
  options,
  allLabel,
  clearLabel,
  className,
}: {
  label: string;
  value?: T;
  onChange: (value?: T) => void;
  options: FilterOption<T>[];
  /** The unfiltered choice at the top of the menu ("Tous les types"). */
  allLabel: string;
  /** The cross's accessible name ("Retirer le filtre Type"). */
  clearLabel: string;
  className?: string;
}) {
  const selected = options.find((option) => option.value === value);
  return (
    <div className={cn("inline-flex min-w-0", className)}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            size="sm"
            variant={selected ? "default" : "outline"}
            aria-label={label}
            className={cn("min-w-0", selected && "rounded-r-none pr-2")}
          >
            <span className="truncate">{selected ? selected.label : label}</span>
            {!selected && <CaretDownIcon weight="bold" className="size-3" />}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuRadioGroup
            value={value ?? ALL}
            onValueChange={(next) => onChange(next === ALL ? undefined : (next as T))}
          >
            <DropdownMenuRadioItem value={ALL}>{allLabel}</DropdownMenuRadioItem>
            {options.map((option) => (
              <DropdownMenuRadioItem key={option.value} value={option.value}>
                {option.label}
                {option.count !== undefined && (
                  <span className="ml-auto pl-3 text-xs tabular-nums text-muted-foreground">
                    {option.count}
                  </span>
                )}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>
      {selected && (
        <Button
          type="button"
          size="sm"
          aria-label={clearLabel}
          onClick={() => onChange(undefined)}
          className="rounded-l-none border-l border-primary-foreground/25 pl-1.5 pr-2"
        >
          <XIcon weight="bold" className="size-3" />
        </Button>
      )}
    </div>
  );
}

/**
 * `FilterMenuPill` for a dimension where several values make sense together (a
 * list of submissions "pending or accepted"). The menu stays open while values
 * are ticked; nothing ticked, or every value, is no filter. Set, the pill reads as
 * its values ("En attente, Acceptée") and its cross clears it.
 */
export function FilterMultiMenuPill<T extends string>({
  label,
  values,
  onChange,
  options,
  allLabel,
  clearLabel,
  className,
}: {
  label: string;
  /** The values kept; `undefined` (or all of them) is no filter. */
  values?: T[];
  onChange: (values?: T[]) => void;
  options: FilterOption<T>[];
  /** The unfiltered choice at the top of the menu ("Tous les statuts"). */
  allLabel: string;
  /** The cross's accessible name ("Retirer le filtre Statut"). */
  clearLabel: string;
  className?: string;
}) {
  const chosen = options.filter((option) => values?.includes(option.value));
  const active = chosen.length > 0 && chosen.length < options.length;

  const toggle = (value: T, on: boolean) => {
    const current = values?.length ? values : options.map((o) => o.value);
    const next = options
      .map((option) => option.value)
      .filter((v) => (v === value ? on : current.includes(v)));
    onChange(next.length === 0 || next.length === options.length ? undefined : next);
  };

  return (
    <div className={cn("inline-flex min-w-0", className)}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            size="sm"
            variant={active ? "default" : "outline"}
            aria-label={label}
            className={cn("min-w-0", active && "rounded-r-none pr-2")}
          >
            <span className="truncate">
              {active
                ? chosen.map((option, index) => (
                    <span key={option.value}>
                      {index > 0 && ", "}
                      {option.label}
                    </span>
                  ))
                : label}
            </span>
            {!active && <CaretDownIcon weight="bold" className="size-3" />}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuCheckboxItem
            checked={!active}
            onCheckedChange={() => onChange(undefined)}
            onSelect={(event) => event.preventDefault()}
          >
            {allLabel}
          </DropdownMenuCheckboxItem>
          <DropdownMenuSeparator />
          {options.map((option) => (
            <DropdownMenuCheckboxItem
              key={option.value}
              checked={!active || values!.includes(option.value)}
              onCheckedChange={(on) => toggle(option.value, on === true)}
              onSelect={(event) => event.preventDefault()}
            >
              {option.label}
              {option.count !== undefined && (
                <span className="ml-auto pl-3 text-xs tabular-nums text-muted-foreground">
                  {option.count}
                </span>
              )}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
      {active && (
        <Button
          type="button"
          size="sm"
          aria-label={clearLabel}
          onClick={() => onChange(undefined)}
          className="rounded-l-none border-l border-primary-foreground/25 pl-1.5 pr-2"
        >
          <XIcon weight="bold" className="size-3" />
        </Button>
      )}
    </div>
  );
}
